<template>
  <div class="pass-form-block">
    <div class="row">
      <div
        v-if="visibleVisitorsCategoryField"
        :class="'col-md-' + fieldWidth(PassSectionFieldAliases.VISITORS_CATEGORY)"
      >
        <visitors-category-input
          v-model="application.visitors_category"
          :input-label="fieldTitle(PassSectionFieldAliases.VISITORS_CATEGORY)"
          :input-hint="fieldHint(PassSectionFieldAliases.VISITORS_CATEGORY)"
          :items="visitorCategories"
          :disabled="disabledInput"
          @change-visitors-category="changeVisitorsCategory()"
        />
      </div>
    </div>
    <div class="row">
      <div v-if="visiblePassKindField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.PASS_KIND)">
        <pass-kind-input
          v-model="application.pass_kind"
          :input-label="fieldTitle(PassSectionFieldAliases.PASS_KIND)"
          :input-hint="fieldHint(PassSectionFieldAliases.PASS_KIND)"
          :form="form"
          :errors="errors"
          :items="availablePassKinds"
          :disabled="disabledPassKinds"
          @change="changePassKind"
        />
      </div>
      <div v-if="visiblePassPurposeField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.APPLICATION_PURPOSE)">
        <pass-purpose-input
          v-model="application.application_group_purpose_id"
          :input-label="fieldTitle(PassSectionFieldAliases.APPLICATION_PURPOSE)"
          :input-hint="fieldHint(PassSectionFieldAliases.APPLICATION_PURPOSE)"
          :form="form"
          :errors="errors"
          :items="calculatedApplicationPurposes()"
          :disabled="disabledApplicationPurpose"
          @change="changePassPurpose"
        />
      </div>
      <div v-if="visibleTerminalIdField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.TERMINAL_ID)">
        <terminal-id-input
          v-model="application.terminal_id"
          :input-label="fieldTitle(PassSectionFieldAliases.TERMINAL_ID)"
          :input-hint="fieldHint(PassSectionFieldAliases.TERMINAL_ID)"
          :form="form"
          :errors="errors"
          :items="terminals"
          :disabled="disabledTerminalIdInput"
          @change="getAllowedTerminalSectors"
        />
      </div>
      <div v-if="visibleTerminalRegionField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.TERMINAL_SECTOR)">
        <terminal-sector-input
          v-if="showTerminalSectorInput"
          :input-label="fieldTitle(PassSectionFieldAliases.TERMINAL_SECTOR)"
          :input-hint="fieldHint(PassSectionFieldAliases.TERMINAL_SECTOR)"
          :items="terminalSectors"
          :disabled="disabledSectorInput"
          :raw-sector-list="rawSectorList"
          :terminal-region-sectors="terminalRegionSectors"
          :form="form"
          :errors="errors"
          @change="$emit('handle-terminal-region'); change('application terminal region'); changeTerminalId($event)"
        />
        <terminal-region-input
          v-if="!showTerminalSectorInput"
          v-model="application.terminal_region_id"
          :form="form"
          :errors="errors"
          :items="calculatedTerminalRegions()"
          :disabled="disabledRegion"
          @change="$emit('handle-terminal-region'); change('application terminal region'); changeTerminalId($event)"
        />
      </div>
      <div
        v-if="visibleAgentOrganizationField"
        :class="'col-md-' + fieldWidth(PassSectionFieldAliases.AGENT_ORGANIZATION)"
      >
        <agent-organization-input
          v-model="application.agent_organization_id"
          :input-label="fieldTitle(PassSectionFieldAliases.AGENT_ORGANIZATION)"
          :input-hint="fieldHint(PassSectionFieldAliases.AGENT_ORGANIZATION)"
          :form="form"
          :errors="errors"
          :items="agentOrganizations"
          :disabled="disabledAgentOrganization"
          @change="change('application agent organization')"
        />
      </div>
      <div
        v-if="visibleTerminalSubdivisionField"
        :class="'col-md-' + fieldWidth(PassSectionFieldAliases.TERMINAL_SUBDIVISION)"
      >
        <terminal-subdivision-input
          v-model="application.subdivision_id"
          :input-label="fieldTitle(PassSectionFieldAliases.TERMINAL_SUBDIVISION)"
          :input-hint="fieldHint(PassSectionFieldAliases.TERMINAL_SUBDIVISION)"
          :form="form"
          :errors="errors"
          :items="computedTerminalSubdivisions"
          :disabled="disabledSubdivision"
          @change="change('application terminal subdivision')"
        />
      </div>
    </div>
    <div class="row">
      <div v-if="visiblePeriodCountField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.TIME_TYPE)">
        <period-count-input
          v-model="application.time_type"
          :input-label="fieldTitle(PassSectionFieldAliases.TIME_TYPE)"
          :input-hint="fieldHint(PassSectionFieldAliases.TIME_TYPE)"
          :form="form"
          :errors="errors"
          :items="availableTerminalTariffTimeTypes"
          :disabled="disabledPeriodCount"
          @change="changePeriodCount($event)"
        />
      </div>
      <div v-if="visiblePeriodOfField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.PERIOD_OF)">
        <period-of-input
          v-model="application.period_of"
          :input-label="fieldTitle(PassSectionFieldAliases.PERIOD_OF)"
          :input-hint="fieldHint(PassSectionFieldAliases.PERIOD_OF)"
          :form="form"
          :errors="errors"
          :disabled="disabledPeriodOf"
          :min-date="startDatePeriod.from"
          :max-date="startDatePeriod.to"
          :disabled-dates="startDatePeriod.disabledDates"
          @change="changePeriodOf"
        />
      </div>
      <div v-if="visiblePeriodToField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.PERIOD_TO)">
        <period-to-input
          v-model="application.period_to"
          :input-label="fieldTitle(PassSectionFieldAliases.PERIOD_TO)"
          :input-hint="fieldHint(PassSectionFieldAliases.PERIOD_TO)"
          :form="form"
          :errors="errors"
          :disabled="disabledPeriodTo || isView"
          :min-date="endDatePeriod.from"
          :max-date="endDatePeriod.to"
          :disabled-dates="endDatePeriod.disabledDates"
          @change="changePeriodTo"
        />
      </div>
    </div>
    <div class="row" v-if="showPassTimeIntervalBlock">
      <div v-if="visibleTimeIntervalField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.TIME_INTERVAL)">
        <time-interval-input
          v-model="application.pass_access"
          :input-label="fieldTitle(PassSectionFieldAliases.TIME_INTERVAL)"
          :input-hint="fieldHint(PassSectionFieldAliases.TIME_INTERVAL)"
          :items="calculatedPassAccesses"
          :disabled="disabledTimeIntervalBlock"
          @change="changePassAccess()"
        />
      </div>
      <div v-if="visibleTimeOfField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.TIME_OF)">
        <time-of-input
          v-model="application.time_of"
          :input-label="fieldTitle(PassSectionFieldAliases.TIME_OF)"
          :input-hint="fieldHint(PassSectionFieldAliases.TIME_OF)"
          :disabled="disablePassAccessTime || this.disabledInput"
          @input="changeTime"
        />
      </div>
      <div v-if="visibleTimeToField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.TIME_TO)">
        <time-to-input
          v-model="application.time_to"
          :form="form"
          :input-label="fieldTitle(PassSectionFieldAliases.TIME_TO)"
          :input-hint="fieldHint(PassSectionFieldAliases.TIME_TO)"
          :disabled="disablePassAccessTime || this.disabledInput"
          @input="changeTime"
        />
      </div>
    </div>
    <div class="row">
      <div
        v-if="visibleAdditionalPurposeField"
        :class="'col-md-' + fieldWidth(PassSectionFieldAliases.ADDITIONAL_PURPOSE)"
      >
        <pass-additional-purpose-input
          v-model="application.additional_purpose"
          :input-label="fieldTitle(PassSectionFieldAliases.ADDITIONAL_PURPOSE)"
          :input-hint="fieldHint(PassSectionFieldAliases.ADDITIONAL_PURPOSE)"
          :form="form"
          :errors="errors"
          :is-contract-fields-display="isCurrentContractFieldsDisplay"
          :disabled="disabledAdditionalPurpose"
          @change="change('application additional purpose')"
        />
      </div>
      <div v-if="visiblePaymentTypeField" :class="'col-md-' + fieldWidth(PassSectionFieldAliases.PAYMENT_TYPE)">
        <payment-type-input
          v-model="application.payment_type_id"
          :input-label="fieldTitle(PassSectionFieldAliases.PAYMENT_TYPE)"
          :input-hint="fieldHint(PassSectionFieldAliases.PAYMENT_TYPE)"
          :errors="errors"
          :items="this.paymentTypes"
          :disabled="disabledInput"
        />
      </div>
    </div>
    <div v-if="activity" class="row">
      <div class="col-md-12">
        <div class="form-group">
          <label class="control-label">Вид деятельности</label>
          <div>{{ activity }}</div>
        </div>
      </div>
    </div>
    <div v-if="displayVesselBlock" class="row">
      <div class="col-md-8">
        <vessel-access-input
          v-model="application.vessel_access_id"
          :form="form"
          :errors="errors"
          :items="vesselAccesses"
          :application-purposes="applicationPurposes"
          :disabled="disabledInput"
          @change="changePassKind"
        />
      </div>
      <div class="col-md-4">
        <div class="vessels-add block" v-if="!isView && canAddVessel">
          <div class="addNewVessel " id="form-vessels-button">
            <button type="button" class="btn btn-success btn-primary-white" @click="addVessel">Добавить судно</button>
          </div>
        </div>
      </div>
    </div>
    <vessels-form-block
      :vessels="form.vessels"
      :form="form"
      :errors="errors"
      :application="form.application"
      :personal-nationalities="personalNationalities"
      :vessel-berth-book="vesselBerthBook"
      :vessel-accesses="vesselAccesses"
      :region-vessel-access="regionVesselAccess"
      :application-purpose="applicationPurpose"
    />
    <div class="row">
      <div v-for="field in orderedCustomFields()" :key="field.id">
        <div v-if="displayCustomField(field)" :class="'col-md-' + field.width * 4">
          <app-custom-field-input
            v-model="form.application.custom_fields[field.id]"
            :errors="errors"
            :items="field.attributes"
            :custom-field="field"
            :form="form"
            :disabled="disabledInput"
          />
        </div>
      </div>
    </div>
    <application-documents-block
      :application="form.application"
      :applicationDocuments="applicationDocuments"
      :form="form"
      :errors="errors"
    />
  </div>
</template>

<script>
import AgentOrganizationInput from '@/common/components/application/form-inputs/pass/AgentOrganizationInput'
import ApplicationDocumentsBlock from '@/common/components/application/ApplicationDocumentsBlock'
import applicationFormContextMixin from '@/common/mixins/applicationFormContextMixin'
import applicationFormMixin from '@/common/mixins/applicationFormMixin'
import { firstElement } from '@/common/helpers'
import { Logger } from '@/common/helpers/Logger'
import mainMixin from '@/common/mixins/mainMixin'
import moment from 'moment-timezone'
import passAccessMixin from '@/common/mixins/passAccessMixin'
import PassAdditionalPurposeInput from '@/common/components/application/form-inputs/pass/PassAdditionalPurposeInput'
import PassKindInput from '@/common/components/application/form-inputs/pass/PassKindInput'
import PassPurposeInput from '@/common/components/application/form-inputs/pass/PassPurposeInput'
import PeriodCountInput from '@/common/components/application/form-inputs/pass/PeriodCountInput'
import PeriodOfInput from '@/common/components/application/form-inputs/pass/PeriodOfInput'
import PeriodToInput from '@/common/components/application/form-inputs/pass/PeriodToInput'
import { PurposeNeedVesselTypes } from '@/common/enums/PurposeNeedVesselTypes'
import TerminalRegionInput from '@/common/components/application/form-inputs/pass/TerminalRegionInput'
import TerminalSubdivisionInput from '@/common/components/application/form-inputs/pass/TerminalSubdivisionInput'
import TimeIntervalInput from '@/common/components/application/form-inputs/pass/TimeIntervalInput'
import TimeOfInput from '@/common/components/application/form-inputs/pass/TimeOfInput'
import TimeToInput from '@/common/components/application/form-inputs/pass/TimeToInput'
import Transport from '@/common/classes/Transport'
import { VesselAccesses as VesselAccessesEnum } from '@/common/enums/VesselAccesses'
import VesselAccessInput from '@/common/components/application/form-inputs/pass/VesselAccessInput.vue'
import VesselsFormBlock from '@/common/components/application/form-blocks/vessels/VesselsFormBlock'
import Visitor from '@/common/classes/Visitor'
import { PassKinds } from '@/common/enums/PassKinds'
import { ApiClient } from '@/helpers/api-client/ApiClient'
import TerminalSectorInput from '@/common/components/application/form-inputs/pass/TerminalSectorInput.vue'
import Sector from '@/common/classes/Sector'
import AppCustomFieldInput from '@/common/components/application/form-inputs/pass/AppCustomFieldInput.vue'
import { PassSectionFieldAliases } from '@/common/enums/PassSectionFieldAliases'
import VisitorsCategoryInput from '@/common/components/application/form-inputs/pass/VisitorsCategoryInput.vue'
import PaymentTypeInput from '@/common/components/application/form-inputs/pass/PaymentTypeInput.vue'
import PassSectionsEnum from '@/common/enums/PassSectionsEnum'
import TerminalIdInput from '@/common/components/application/form-inputs/pass/TerminalIdInput.vue'
import { mapState } from 'vuex';

export default {
  name: 'PassFormBlock',

  components: {
    TerminalIdInput,
    PaymentTypeInput,
    VisitorsCategoryInput,
    TerminalSectorInput,
    AppCustomFieldInput,
    VesselAccessInput,
    TimeToInput,
    TimeOfInput,
    TimeIntervalInput,
    PeriodCountInput,
    PeriodOfInput,
    PeriodToInput,
    PassKindInput,
    PassPurposeInput,
    TerminalRegionInput,
    AgentOrganizationInput,
    ApplicationDocumentsBlock,
    TerminalSubdivisionInput,
    PassAdditionalPurposeInput,
    VesselsFormBlock
  },

  mixins: [
    mainMixin,
    applicationFormMixin,
    applicationFormContextMixin,
    passAccessMixin
  ],

  data: () => {
    return {
      terminals: [],
      terminalSectors: [],
      rawSectorList: []
    }
  },

  model: {
    prop: 'application',
    event: 'change-application'
  },

  props: {
    activityOrganizations: {
      type: [Array, Object],
      default: () => []
    },

    agentOrganizations: {
      type: [Array, Object],
      default: () => []
    },

    paymentTypes: {
      type: [Array, Object],
      default: () => []
    },

    application: {
      type: Object,
      required: true
    },

    applicationDocuments: {
      type: Array,
      default: () => []
    },

    applicationPurposes: {
      type: [Array, Object],
      default: () => []
    },

    personalNationalities: {
      type: [Array, Object],
      default: () => []
    },

    purposeSubdivisions: {
      type: [Array, Object],
      default: () => []
    },

    passAccesses: {
      type: [Array, Object],
      default: () => []
    },

    terminalRegions: {
      type: [Array, Object],
      default: () => []
    },

    applicationCustomFields: {
      type: [Array, Object],
      default: () => []
    },

    terminalRegionSectors: {
      type: [Array, Object],
      default: () => []
    },

    terminalSubdivisions: {
      type: [Array, Object],
      default: () => []
    },

    terminalTariffPeriods: {
      type: [Array, Object],
      default: () => []
    },

    terminalTariffTimeTypes: {
      type: [Object],
      default: () => {
      }
    },

    timezone: {
      type: String,
      default: 'Europe/Moscow'
    },

    passKinds: {
      type: [Object],
      default: () => []
    },

    cfgFormFields: {
      type: [Array, Object],
      default: () => []
    },

    vesselAccesses: {
      type: [Array, Object],
      default: () => []
    },

    vesselBerthBook: {
      type: [Array, Object],
      default: () => []
    },

    visitorCategories: {
      type: Array,
      default: () => []
    },

    visitorCategoryPurposes: {
      type: [Object, Array],
      default: () => {
      }
    }
  },

  computed: {
    ...mapState([
      'creationPassKind'
    ]),

    getAllowedSectorsOnCreated() {
      return (((this.isDuplicate || this.isDraft || this.isView) && this.application.application_group_purpose_id) ||
        ((this.isNew || this.isDraft) && this.terminalSectorsEnabled))
    },

    PassSectionFieldAliases() {
      return PassSectionFieldAliases
    },

    showPaymentTypeInput() {
      if (this.fieldVisible(PassSectionFieldAliases.PAYMENT_TYPE, PassSectionsEnum.pass)) {
        let currentPurpose = this.applicationPurposes[this.form.application.application_group_purpose_id]

        return currentPurpose && Number(currentPurpose.payment_free) !== 1
      }

      return false
    },

    activity() {
      const targetActivityOrganization = this.activityOrganizations
        .find(activityOrganization => activityOrganization.organization_inn === this.form.organization.inn)

      if (targetActivityOrganization) {
        return targetActivityOrganization.activity.name
      }

      if (this.application.application_group_purpose_id === null) {
        return undefined
      }

      let purposeActivities = this.applicationPurposes[this.application.application_group_purpose_id]?.activities ?? []
      purposeActivities = purposeActivities.filter((activity) => [this.application.terminal_region_id, null, undefined].includes(activity.region_id))

      return purposeActivities[0]?.name
    },

    applicationPurpose() {
      return this.applicationPurposes[this.application.application_group_purpose_id]
    },

    applicationPurposeCanHaveVessel() {
      if (this.applicationPurpose) {
        let purpose = this.applicationPurpose

        return Number(purpose.need_vessel) !== 0
      }

      return false
    },

    displayVesselBlock() {
      return this.applicationPurposeCanHaveVessel && this.regionVesselAccess
    },

    applicationPurposeSubdivisions() {
      return this.purposeSubdivisions[this.application.application_group_purpose_id] ?? []
    },

    availableTerminalTariffTimeTypes() {
      if (this.applicationPurpose === undefined || this.applicationPurpose?.pass_type === 'all') {

        return this.terminalTariffTimeTypes
      } else {
        const timeType = {}
        timeType[this.applicationPurpose.pass_type] = this.terminalTariffTimeTypes[this.applicationPurpose.pass_type]
        return timeType
      }
    },

    availablePassKinds() {
      let arr = []
      let purposesArr = Object.values(this.applicationPurposes)
      purposesArr.forEach((purpose) => {
        purpose.passKinds.forEach(element => {
          if (this.passKinds[element] && !arr.includes(this.passKinds[element])) {
            arr.push(this.passKinds[element])
          }
        })
      })

      arr.sort(function (a, b) {
        return a.sort - b.sort
      })

      return arr
    },

    disabledPassKinds() {
      return this.disabledInput || this.availablePassKinds.length === 1
    },

    canAddVessel() {
      return this.form.application.vessel_access_id !== VesselAccessesEnum.WITHOUT_VESSEL
    },

    calculatedPassAccesses() {
      const allowedAccesses = [this.application.time_type, 'all']
      return this.passAccesses.filter(
        passAccess => allowedAccesses.includes(passAccess.time_type)
      )
    },

    currentPassAccess() {
      return this.passAccesses.find((item) => parseInt(item.id) === parseInt(this.form.application.pass_access))
    },

    showPassTimeIntervalBlock() {
      return this.calculatedPassAccesses.length > 0
    },

    computedTerminalSubdivisions() {
      if (this.isView) {
        return this.terminalSubdivisions.filter(subdivision =>
          subdivision.id === this.application.subdivision_id
        )
      }

      let subdivisionsForOrganization = this.terminalSubdivisions.filter(subdivision => {
        return this.applicationPurposeSubdivisions.find(el => el.subdivision_id === subdivision.id && el.organization_inn === this.form.organization.inn) &&
          (Number(this.application.terminal_id) === Number(subdivision.terminal_id) || subdivision.terminal_id === null)
      })
      if (subdivisionsForOrganization.length !== 0) {
        return subdivisionsForOrganization
      }

      return this.terminalSubdivisions.filter(subdivision => {
        return this.applicationPurposeSubdivisions.find(el => el.subdivision_id === subdivision.id && el.organization_inn === null) &&
          (Number(this.application.terminal_id) === Number(subdivision.terminal_id) || subdivision.terminal_id === null)
      })
    },

    disabledTimeIntervalBlock() {
      return this.disabledInput || this.calculatedPassAccesses.length === 1
    },

    disablePassAccessTime() {
      return Boolean(this.currentPassAccess.fixed) === true
    },

    disabledAdditionalPurpose() {
      return Boolean(this.disabledInput || !this.needAdditionalPurpose)
    },

    disabledAgentOrganization() {
      return Boolean(this.disabledInput)
    },

    disabledApplicationPurpose() {
      return Boolean(this.disabledInput) || this.calculatedApplicationPurposes().length === 1 || !this.form.application.pass_kind
    },

    disabledTerminalIdInput() {
      return Boolean(this.disabledInput) || this.terminals.length === 1 || !this.application.application_group_purpose_id
    },

    disabledPeriodCount() {
      return Boolean(this.disabledInput ||
        (this.applicationPurpose && this.applicationPurpose.pass_type !== 'all') ||
        Object.keys(this.availableTerminalTariffTimeTypes).length === 1)
    },

    disabledPeriodOf() {
      return Boolean(this.disabledInput)
    },

    disabledPeriodTo() {
      return this.timeType?.fixed_count_days && this.user.role !== 'supervisor'
    },

    disabledSectorInput() {
      return Boolean(this.isView || this.terminalSectors.length === 1 ||
        (!this.visibleTerminalIdField && !this.application.application_group_purpose_id) ||
        (this.visibleTerminalIdField && this.application.terminal_id === null)
      )
    },

    disabledRegion() {
      return Boolean(
        this.disabledInput ||
        (!this.visibleTerminalIdField && !this.application.application_group_purpose_id) ||
        (this.visibleTerminalIdField && this.application.terminal_id === null) ||
        this.calculatedTerminalRegions().length <= 1
      )
    },

    disabledSubdivision() {
      return Boolean(
        this.disabledInput ||
        this.computedTerminalSubdivisions.length <= 1 ||
        this.application.application_group_purpose_id === null
      )
    },

    needAdditionalPurpose() {
      return this.applicationPurpose?.need_additional_purpose
    },

    rangeTimeType() {
      const validate = !((this.user.role === 'supervisor' && this.isEdit) || this.isView)

      return validate ? this.timeType : null
    },

    regionVesselAccess() {
      return Boolean(this.terminalRegion?.vessel_access)
    },

    timeType() {
      return this.terminalTariffTimeTypes[this.form.application.time_type] ?? null
    },

    terminalSectorsEnabled() {
      return Number(this.configuration.use_sectors) === 1
    },

    showTerminalSectorInput() {
      return this.terminalSectorsEnabled && !(this.isView && this.application.terminal_sectors.length === 0)
    },

    visibleVisitorsCategoryField() {
      return this.fieldVisible(PassSectionFieldAliases.VISITORS_CATEGORY, PassSectionsEnum.pass)
    },

    visiblePassPurposeField() {
      return this.fieldVisible(PassSectionFieldAliases.APPLICATION_PURPOSE, PassSectionsEnum.pass)
    },

    visibleTerminalIdField() {
      return this.fieldVisible(PassSectionFieldAliases.TERMINAL_ID, PassSectionsEnum.pass)
    },

    visibleTerminalRegionField() {
      return this.fieldVisible(PassSectionFieldAliases.TERMINAL_SECTOR, PassSectionsEnum.pass)
    },

    visibleAgentOrganizationField() {
      return this.needAgentCheck && this.fieldVisible(PassSectionFieldAliases.AGENT_ORGANIZATION, PassSectionsEnum.pass)
    },

    visibleTerminalSubdivisionField() {
      return !this.needAgentCheck &&
        this.fieldVisible(PassSectionFieldAliases.TERMINAL_SUBDIVISION, PassSectionsEnum.pass)
    },

    visiblePeriodCountField() {
      return this.fieldVisible(PassSectionFieldAliases.TIME_TYPE, PassSectionsEnum.pass)
    },

    visiblePeriodOfField() {
      return this.fieldVisible(PassSectionFieldAliases.PERIOD_OF, PassSectionsEnum.pass)
    },

    visiblePeriodToField() {
      return this.fieldVisible(PassSectionFieldAliases.PERIOD_TO, PassSectionsEnum.pass)
    },

    visibleTimeIntervalField() {
      return this.fieldVisible(PassSectionFieldAliases.TIME_INTERVAL, PassSectionsEnum.pass)
    },

    visibleTimeOfField() {
      return this.fieldVisible(PassSectionFieldAliases.TIME_OF, PassSectionsEnum.pass)
    },

    visibleTimeToField() {
      return this.fieldVisible(PassSectionFieldAliases.TIME_TO, PassSectionsEnum.pass)
    },

    visiblePassKindField() {
      return this.fieldVisible(PassSectionFieldAliases.PASS_KIND, PassSectionsEnum.pass)
    },

    visibleAdditionalPurposeField() {
      return this.needAdditionalPurpose &&
        this.fieldVisible(PassSectionFieldAliases.ADDITIONAL_PURPOSE, PassSectionsEnum.pass)
    },

    visiblePaymentTypeField() {
      return this.showPaymentTypeInput &&
        this.fieldVisible(PassSectionFieldAliases.PAYMENT_TYPE, PassSectionsEnum.pass)
    },

    withoutNeedSubdivisionCheck() {
      return Boolean(
        this.application.application_group_purpose_id && this.applicationPurpose?.need_subdivision_check === 0
      )
    },

    applicationPurposeId() {
      return this.application.application_group_purpose_id
    }
  },

  created() {
    this.setPassAccess()
    this.setPassKind()
    if (this.getAllowedSectorsOnCreated) {
      this.getAllowedTerminalSectors()
    }
  },

  watch: {
    applicationPurposeId() {
      this.changePassPurpose()
    },
    displayVesselBlock(value) {
      if (value === false) {
        this.application.vessel_access_id = VesselAccessesEnum.WITHOUT_VESSEL
      }
    },
    applicationPurposes() {
      if (!this.applicationPurposes[this.applicationPurposeId]) {
        this.changePassKind()
      }
    }
  },

  mounted() {
    if (!this.isView) {
      this.changePeriodOf()
      if (this.isDuplicate || this.isDraft) {
        if (!this.computedTerminalSubdivisions?.find(s => s.id === this.application.subdivision_id)) {
          this.application.subdivision_id = null
        }
      }
    }
  },

  methods: {
    changeTime(value) {
      this.$emit('input', value)
      this.setPeriodTo(moment.tz(this.form.application.period_to, 'DD.MM.YYYY', this.timezone))
    },

    setPassKind() {
      if (this.isNew) {
        let passKinds = this.availablePassKinds

        this.form.application.pass_kind = passKinds[0].id

        if (this.calculatedApplicationPurposes().length === 1) {
          this.application.application_group_purpose_id = this.calculatedApplicationPurposes()[0].id
        }
      }
    },

    calculatedApplicationPurposes() {
      let currentPassKind = this.form.application.pass_kind
      let purposes = Object.values(this.applicationPurposes)

      return purposes.filter((item) => item.passKinds.findIndex((element) => element === currentPassKind) !== -1)
    },

    calculatedTerminalRegions() {
      const { pass_kind, terminal_id } = this.application;
      const passKindFilters = {
        [PassKinds.TRANSPORT]: item => Number(item.transport_access) !== 0
      };

      let regions = Object.values(this.terminalRegions);
      if (this.visibleTerminalIdField) {
        regions = regions.filter(item => item.terminal_id === terminal_id);
      }
      const kindFilter = passKindFilters[pass_kind];

      return kindFilter ? regions.filter(kindFilter) : regions;
    },

    changePassAccess() {
      let currentPassAccess = this.currentPassAccess
      this.form.application.time_to = currentPassAccess.time_to
      this.form.application.time_of = currentPassAccess.time_of
    },

    changePassKind() {
      this.form.application.application_group_purpose_id = null
      this.form.application.terminal_region_id = null

      if (this.calculatedApplicationPurposes().length === 1) {
        this.form.application.application_group_purpose_id = this.calculatedApplicationPurposes()[0].id
      }
      if (this.calculatedTerminalRegions().length === 1) {
        this.application.terminal_region_id = this.calculatedTerminalRegions()[0].id
      }
      if (!this.isPassKindMaterial) {
        this.deleteTangibleBlockData()
      }
      if (this.isPassKindPedestrian) {
        this.deleteTransportBlockData()
      }
      if (this.isPassKindTransport) {
        this.deleteVisitorBlockData()
        this.addTransportBlockData()
      }
      if (this.isPassKindMaterial) {
        this.addTangibleBlockData()
      }
      if (this.terminalSectorsEnabled && !this.visibleTerminalIdField) {
        this.form.application.terminal_sectors = []
        this.getAllowedTerminalSectors()
      }
    },

    addTangibleBlockData() {
      if (this.form.application.tangibles.length === 0) {
        this.form.application.tangibles.push({ name: null, identification: null, count: null })
      }
    },

    addVessel() {
      let vesselAccessSetting = this.vesselAccesses.find((item) => item.id === this.form.application.vessel_access_id)
      let flag = vesselAccessSetting.is_rf === 1 ? 182 : null
      let passFlag = vesselAccessSetting.is_rf === 1 ? '/ru/terminal/vue/img/icons/countries/RU.svg' : null
      const vessel = Object.assign({}, {
        berth: null,
        flag: flag,
        name: null,
        passFlag: passFlag
      })
      this.form.vessels.push(vessel)
    },

    deleteTangibleBlockData() {
      this.form.application.tangibles = []
    },

    addTransportBlockData() {
      this.form.visitors[0].transports = [new Transport()]
      if (Number(this.configuration.form_drivers) !== 0) {
        this.form.visitors[0].transports[0].drivers = [new Visitor()]
      }
    },

    deleteTransportBlockData() {
      this.form.visitors.forEach(element => {
        element.transports = []
      })
    },

    deleteVisitorBlockData() {
      this.form.visitors = [new Visitor()]
    },

    displayCustomField(field) {
      return (Number(field.visible) !== 0 && [this.form.application?.pass_kind, 'all'].includes(field?.pass_kind)) ||
        (this.isView && !!this.form.application.custom_fields[field.id])
    },

    change(inputName) {
      Logger.log(`Changed ${inputName}!`)
      this.$emit('change-application', this.application)
    },

    changePassPurpose() {
      if (this.isPassKindTransport) {
        this.addTransportBlockData()
      }
      if (this.isPassKindMaterial) {
        this.addTangibleBlockData()
      }
      if (!this.isPassKindMaterial) {
        this.deleteTangibleBlockData()
      }

      const timeType = this.applicationPurpose?.pass_type

      if (timeType !== undefined && timeType !== 'all') {
        this.$set(this.application, 'time_type', this.applicationPurpose.pass_type)
        this.changePeriodCount(timeType)
      }

      if (this.application.application_group_purpose_id) {
        this.application.subdivision_id = null
        this.application.terminal_region_id = null
      }

      if (this.applicationPurpose?.need_terminal) {
        this.application.additional_terminal_region = ''
      } else {
        this.application.terminal_region_id =
          this.applicationPurpose?.terminal_region
      }

      this.application.need_additional_purpose = this.applicationPurpose?.need_additional_purpose
      this.application.need_type = this.applicationPurpose?.need_type
      switch (this.applicationPurpose?.need_vessel) {
        case PurposeNeedVesselTypes.FORBIDDEN_VESSEL:
          this.form.application.vessel_access_id = VesselAccessesEnum.WITHOUT_VESSEL
          break
        case PurposeNeedVesselTypes.FREE_VESSEL:
          this.form.application.vessel_access_id = (this.vesselAccesses.filter((item) => Number(item.visible) === 1))[0].id
          break
        case PurposeNeedVesselTypes.NEED_VESSEL:
          this.form.application.vessel_access_id = (this.vesselAccesses.filter((item) => Number(item.visible) === 1 && item.id !== VesselAccessesEnum.WITHOUT_VESSEL))[0].id
          break
        default:
          this.form.application.vessel_access_id = VesselAccessesEnum.WITHOUT_VESSEL
      }

      this.application.need_agent_check = this.applicationPurpose?.need_agent_check

      if (Number(this.form.application.need_agent_check) === 0) {
        this.application.agent_organization_id = null
      }

      if (this.application.vessel_access_id === VesselAccessesEnum.WITHOUT_VESSEL || !this.regionVesselAccess) {
        this.form.vessels = []
      }

      this.form.tangibles = []
      if (this.terminalSectorsEnabled) {
        this.form.application.terminal_sectors = []
        this.getAllowedTerminalSectors()
      } else if (this.visibleTerminalIdField) {
        this.getAllowedTerminals()
      }

      this.$nextTick(() => {
        if (this.terminalRegions.length === 1) {
          const terminalRegion = firstElement(this.terminalRegions)
          this.application.terminal_region_id = terminalRegion.id
          this.changeTerminalId(terminalRegion.id)
          this.$emit('handle-terminal-region')
        }
      })
    },

    changePeriodCount(value) {
      this.application.period_count = this.terminalTariffTimeTypes[value]?.period_count ?? 1
      this.setPassAccess()
      this.emitChangeDate()

      if (!this.application.application_group_purpose_id) {
        this.application.application_group_purpose_id = null
        this.application.terminal_region_id = null
      }
    },

    async getAllowedTerminals() {
      let purposeId = this.form.application.application_group_purpose_id
      if ((this.isNew || this.isDraft) && this.calculatedApplicationPurposes().length === 1) {
        purposeId = this.calculatedApplicationPurposes()[0].id
      }
      await ApiClient.allowedTerminals({
        application_group_purpose_id: purposeId
      }).then((response) => {
        if (response.data?.data?.allowedTerminals) {
          this.terminals = response.data?.data?.allowedTerminals
        }
      })
    },

    async getAllowedTerminalSectors() {
      if (this.visibleTerminalIdField) {
        await this.getAllowedTerminals()
      }

      let purposeId = this.form.application.application_group_purpose_id
      if ((this.isNew || this.isDraft) && this.calculatedApplicationPurposes().length === 1) {
        purposeId = this.calculatedApplicationPurposes()[0].id
      }
      await ApiClient.allowedTerminalSectors({
        application_group_purpose_id: purposeId,
        terminal_id: this.form.application.terminal_id
      }).then((response) => {
        if (response.data?.data?.allowedTerminalSectors) {
          this.terminalSectors = response.data?.data?.allowedTerminalSectors
        }

        let sectorsList = this.terminalSectors.map((terminalSector) => {
          return new Sector(terminalSector)
        })

        sectorsList.forEach((item) => {
          let currentSector = item
          while (currentSector.parent_id) {
            item.wayToSector.push(currentSector.parent_id)
            currentSector = sectorsList.find((item) => item.id === currentSector.parent_id)
          }
        })

        this.rawSectorList = sectorsList

        let initialSectors = sectorsList.filter((item) => item.parent_id === null)
        initialSectors.forEach((item) => {
          this.setChildrenToSector(item, sectorsList)
        })
        this.terminalSectors = initialSectors
      })
    },

    setChildrenToSector(sector, sectorsList) {
      let childrenSectors = sectorsList.filter((item) => item.parent_id === sector.id)
      if (childrenSectors.length > 0) {
        childrenSectors.forEach((elem) => {
          this.setChildrenToSector(elem, sectorsList)
        })
        sector.children = childrenSectors
      }
      if (childrenSectors.length === 0) {
        delete sector.children
      }
    },

    changePeriodOf(periodOf) {
      debugger
      if (typeof periodOf === 'string' || periodOf instanceof String) {
        this.setPeriodOf(moment.tz(periodOf, 'DD.MM.YYYY', this.timezone))
      }
      this.emitChangeDate(true)
    },

    changePeriodTo(periodTo) {
      if (typeof periodTo === 'string' || periodTo instanceof String) {
        this.setPeriodTo(moment.tz(periodTo, 'DD.MM.YYYY', this.timezone))
      }
      this.emitChangeDate(true)
    },

    changeTerminalId(event) {
      if (!this.visibleTerminalIdField) {
        this.$set(this.application, 'terminal_id', this.terminalRegions.find((x) => parseInt(x.id) === parseInt(event))?.terminal_id)
      }
    },

    changeVisitorsCategory() {
      this.$nextTick().then(() => {
        if (this.terminalSubdivisions.length === 1) {
          this.application.subdivision_id = this.terminalSubdivisions[0].id
        }

        this.$emit('handle-terminal-region')
      })
    },

    emitChangeDate() {
      this.$emit('change-date', ...arguments)
    },

    visibleCustomFields() {
      if (!this.isView) {
        return this.applicationCustomFields.filter((item) => Number(item.visible) === 1)
      }

      return this.applicationCustomFields.filter((item) => this.displayCustomField(item))
    },

    orderedCustomFields() {
      let baseFields = this.visibleCustomFields()
      let resultOrder = []
      let threeColumnWidth = baseFields.filter((item) => Number(item.width) === 3)
      let twoColumnWidth = baseFields.filter((item) => Number(item.width) === 2)
      let oneColumnWidth = baseFields.filter((item) => Number(item.width) === 1)
      resultOrder = [...resultOrder, ...threeColumnWidth]

      while (twoColumnWidth.length > 0) {
        resultOrder.push(twoColumnWidth[0])
        twoColumnWidth.shift()
        if (oneColumnWidth.length > 0) {
          resultOrder.push(oneColumnWidth[0])
          oneColumnWidth.shift()
        }
      }

      resultOrder = [...resultOrder, ...oneColumnWidth]

      return resultOrder
    }
  }
}
</script>
